import { Link } from "react-router-dom";
import { Logo } from "@/components/Logo";
import { publicReleases } from "@/lib/changelog/releases";
import { openCookieSettings } from "@/lib/consent/store";
import { getGuide, guideNavPath } from "@/lib/guides";
import type { GuideKey } from "@/lib/guides/types";
import { makeTr, pick, type LandingLocale, localePath } from "@/lib/landing/locale";
import { Container } from "./primitives";

const linkClass =
  "rounded-sm transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--action))] dark:focus-visible:ring-primary";

/** The guides the footer names; the rest are one click away on the hub. */
const FOOTER_GUIDES: readonly GuideKey[] = [
  "guideAccountants",
  "guideMatching",
  "guideFioApi",
  "guideGpcImport",
  "guideEmailInbox",
  "guideAccountant",
  "toolGpcToCsv",
];

/** `path` as in LandingNav: off the landing page, section links lead back to it. */
export function LandingFooter({ locale, path = "/" }: { locale: LandingLocale; path?: string }) {
  const tr = makeTr(locale);
  const home = path === "/" ? "" : localePath(locale, "/");
  // The newest release with something public to show, as on /changelog.
  const release = publicReleases()[0];
  const columns = [
    {
      title: tr("Zettl", "Zettl"),
      links: [
        {
          href: `${home}#jak-to-funguje`,
          label: tr("Jak to funguje", "How it works"),
        },
        { href: `${home}#cenik`, label: tr("Ceník", "Pricing") },
        { href: `${home}#faq`, label: tr("Časté dotazy", "FAQ") },
        { to: "/changelog", label: tr("Novinky", "What's new") },
        { to: "/auth", label: tr("Přihlásit se", "Sign in") },
      ],
    },
    {
      title: tr("Průvodce", "Guides"),
      links: [
        ...FOOTER_GUIDES.map((key) => ({
          to: guideNavPath(key),
          label: pick(getGuide(key).name, locale),
        })),
        { to: guideNavPath("tools"), label: tr("Nástroje zdarma", "Free tools") },
        { to: guideNavPath("guides"), label: tr("Všechny průvodce", "All guides") },
      ],
    },
    {
      title: tr("Právní", "Legal"),
      links: [
        { to: "/privacy", label: tr("Ochrana osobních údajů", "Privacy policy") },
        { to: "/terms", label: tr("Obchodní podmínky", "Terms of service") },
        { to: "/cookies", label: tr("Cookies", "Cookies") },
        { action: openCookieSettings, label: tr("Nastavení cookies", "Cookie settings") },
        {
          to: "/dpa",
          label: tr("Zpracovatelská smlouva", "Data processing agreement"),
        },
      ],
    },
    {
      title: tr("Kontakt", "Contact"),
      links: [{ to: "/contact", label: tr("Napište nám", "Write to us") }],
    },
  ];

  return (
    <footer>
      <Container className="pb-12 pt-4">
        <div className="flex flex-col gap-10 md:flex-row md:justify-between">
          <div className="max-w-xs">
            <Link to={localePath(locale, "/")} aria-label="Zettl" className={linkClass}>
              <Logo size="sm" />
            </Link>
            <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
              {tr(
                "Párování plateb z Fio banky s doklady pro české firmy a OSVČ.",
                "Matching payments from Fio banka with their documents, for Czech businesses."
              )}
            </p>
          </div>
          <nav
            aria-label={tr("Patička", "Footer")}
            className="grid grid-cols-2 gap-8 sm:grid-cols-4"
          >
            {columns.map((col) => (
              <div key={col.title}>
                <p className="text-xs font-medium text-muted-foreground">{col.title}</p>
                <ul className="mt-3 space-y-2 text-sm">
                  {col.links.map((l) => (
                    <li key={l.label}>
                      {"action" in l && l.action ? (
                        <button type="button" onClick={l.action} className={linkClass}>
                          {l.label}
                        </button>
                      ) : "href" in l && l.href ? (
                        <a href={l.href} className={linkClass}>
                          {l.label}
                        </a>
                      ) : (
                        <Link to={localePath(locale, l.to!)} className={linkClass}>
                          {l.label}
                        </Link>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </nav>
        </div>
        <div className="mt-10 flex flex-col gap-2 border-t pt-6 text-xs text-muted-foreground sm:flex-row sm:justify-between">
          <p className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
            <span>© {new Date().getFullYear()} Zettl</span>
            {release && (
              <>
                <span aria-hidden="true">·</span>
                <span>{`${tr("Verze", "Version")} ${release.version}`}</span>
                <span aria-hidden="true">·</span>
                <Link to={localePath(locale, "/changelog")} className={linkClass}>
                  {tr("Co je nového", "What's new")}
                </Link>
              </>
            )}
          </p>
          <p>
            {tr(
              "Zettl není produktem Fio banky a není s ní spojen.",
              "Zettl is not a product of Fio banka and is not affiliated with it."
            )}
          </p>
        </div>
      </Container>
    </footer>
  );
}
